import { useEffect, useState } from 'react';
import { graphql } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	ConfluenceAccessLinkReleaseStage,
	useAccessLinkReleaseStageQuery$data as UseAccessLinkReleaseStageQueryData,
	useAccessLinkReleaseStageQuery$variables as UseAccessLinkReleaseStageQueryVariables,
} from './__generated__/useAccessLinkReleaseStageQuery.graphql.ts';
import {
	getAccessLinkReleaseStageFlags,
	type AccessLinkReleaseStageFlags,
} from './getAccessLinkReleaseStageFlags.ts';

const accessLinkReleaseStageQuery = graphql`
	query useAccessLinkReleaseStageQuery($cloudId: ID!) {
		confluence_accessLinkReleaseStage(cloudId: $cloudId)
	}
`;

export const createAccessLinkReleaseStageQueryPromise = (
	cloudId: string,
): Promise<ConfluenceAccessLinkReleaseStage | null> => {
	if (
		!UNSAFE_expValEqualsNoExposure(
			'cc_perms_access_link_release_stage_migration',
			'isEnabled',
			true,
		)
	) {
		return Promise.resolve(null);
	}

	return query<UseAccessLinkReleaseStageQueryData, UseAccessLinkReleaseStageQueryVariables>({
		query: accessLinkReleaseStageQuery,
		variables: { cloudId },
		fetchPolicy: 'cache-first',
	})
		.then(({ data, hasErrors }) => {
			if (hasErrors) {
				const error = new Error('useAccessLinkReleaseStageQuery returned errors');
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PERMISSIONS_EXPERIENCE,
				});
				markErrorAsHandled(error);
				return null;
			}
			return data?.confluence_accessLinkReleaseStage ?? null;
		})
		.catch((error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
			markErrorAsHandled(error);
			return null;
		});
};

export type AccessLinkReleaseStage = ConfluenceAccessLinkReleaseStage;

export type UseAccessLinkReleaseStageResult = AccessLinkReleaseStageFlags & {
	accessLinkReleaseStage: AccessLinkReleaseStage | null;
	loading: boolean;
};

export const useAccessLinkReleaseStage = (): UseAccessLinkReleaseStageResult => {
	const { cloudId, isLoggedIn } = useSessionData();
	const shouldResolve =
		isLoggedIn &&
		UNSAFE_expValEqualsNoExposure(
			'cc_perms_access_link_release_stage_migration',
			'isEnabled',
			true,
		);

	const [accessLinkReleaseStage, setAccessLinkReleaseStage] =
		useState<ConfluenceAccessLinkReleaseStage | null>(null);
	const [loading, setLoading] = useState(shouldResolve);

	useEffect(() => {
		if (!shouldResolve) {
			return;
		}

		let isActive = true;
		setLoading(true);

		void createAccessLinkReleaseStageQueryPromise(cloudId).then((stage) => {
			if (!isActive) {
				return;
			}

			setAccessLinkReleaseStage(stage);
			setLoading(false);
		});

		return () => {
			isActive = false;
		};
	}, [cloudId, shouldResolve]);

	return {
		accessLinkReleaseStage,
		...getAccessLinkReleaseStageFlags(accessLinkReleaseStage),
		loading,
	};
};
